import {
  getAttractions,
  getAttractionOffers,
  getContentTagOfferIds,
  getBookingDetailsByIds,
  getExperienceById,
  getExperienceCategories,
  getExperienceDateAvailability,
  getExperienceListFacets,
  getExperienceMetrics,
  getExperiences,
  getExperiencesById,
  getPassportExperienceTicketsById,
  getExperienceSearchList,
  getExperienceTimeAvailability,
  getExperienceTransferAvailability,
  getExperienceVoucher,
  validateExperiencesItems,
  type ExperienceFacetFilters,
} from 'api/experiences'
import { getExperienceSearchStreamList, startExperienceSearchStream, extendExperienceSearchStream, EXPERIENCE_SEARCH_STREAM_PAGE_SIZE, EXPERIENCE_SEARCH_PAGE_STREAM_LISTENER_ID, getExperienceSearchStreamEventGroupKey, type ExperienceSearchStreamParams } from 'api/experienceSearchStream'
import { EXPERIENCE_VIEWED, API_CALL, API_CALL_REQUEST, API_CALL_SUCCESS, API_CALL_FAILURE, SET_EXPERIENCE_PLACE, UPDATE_USER_LAT_LONG, SET_CANCELLING_EXPERIENCES, CLEAR_EXPERIENCE_TIMES, CLEAR_EXPERIENCE, OFFER_SET_EXPERIENCES_SEARCH_RESULT_METADATA, END_EXPERIENCE_LIST_STREAM, SET_EXPERIENCE_LIST_ERROR } from './actionConstants'
import { getAttractionOffersKey, getContentTagOffersKey, getExperienceListKey, getExperienceDatesKey, getExperienceTimesKey, type ExperienceCoordinates } from 'lib/experiences/experienceUtils'
import {
  FETCH_EXPERIENCE,
  FETCH_EXPERIENCE_LIST,
  FETCH_EXPERIENCE_DATES,
  FETCH_EXPERIENCE_TIMESLOTS,
  FETCH_EXPERIENCES,
  FETCH_PASSPORT_EXPERIENCE_TICKETS,
  FETCH_EXPERIENCE_CATEGORIES,
  FETCH_EXPERIENCE_LIST_FACETS,
  FETCH_EXPERIENCE_METRICS,
  FETCH_EXPERIENCE_VOUCHER,
  FETCH_BOOKING_DETAILS,
  FETCH_TRANSFER_OPTIONS,
  FETCH_REFUND_DETAILS,
  VALIDATE_EXPERIENCE_ITEMS,
  FETCH_TOP_ATTRACTIONS,
  FETCH_ATTRACTION_OFFERS,
  FETCH_CONTENT_TAG_OFFERS,
} from './apiActionConstants'
import { addRecentlyViewedExperience } from 'cookies/recentlyViewedExperiencesCookie'
import { unique } from 'lib/array/arrayUtils'
import {
  addStateListener,
  getStateListeners,
  removeAllSearchSocketListenersAndSafelyDisconnect,
  removeStateListenerAndMaybePause,
} from 'lib/search/searchSocketListeners'
import { clearLastMessage } from 'lib/search/socketEmitter'
import uuidV4 from 'lib/string/uuidV4Utils'
import { getPlaceByRegionCode } from 'constants/places'
import { clearExperienceLocationCookie, setExperienceLocationCookie } from 'cookies/experienceLocationCookie'
import getCurrentGeoLocation from 'lib/geo/navigatorUtils'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { getOrderItemsRefundDetails } from 'api/order'
import type { AppAction } from './types/ActionTypes'
import type { OrderExperienceViewTicket } from 'components/Common/OrderView/OrderItemSummaries/OrderExperienceSummaries/OrderExperienceSummary'
import pollExperiencesVouchers from 'lib/polling/pollExperiencesVouchers'
import { EmptyObject } from 'lib/object/objectUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import getObjectKey from 'lib/object/getObjectKey'
import { mapSearchStreamingCategories, mapSearchStreamingFeatures, isExperienceSearchPagePath } from 'lib/experiences/experienceSearchStreaming'

export function setExperienceOffersMetadata(listKey: string, metadata: App.ExperienceOffersMetadata): AppAction {
  return {
    type: OFFER_SET_EXPERIENCES_SEARCH_RESULT_METADATA,
    listKey,
    metadata,
  }
}

export function fetchExperienceSearchList(
  placeId: string | undefined,
  filters: {
    categoryCodes?: Array<number>
    priceLte?: number
    priceGte?: number
    sortBy?: Experiences.SortTypes
    campaigns?: Array<string>
    placeIdToIgnore?: string
    bounds?: string
    luxPlusFeatures?: Array<App.LuxPlusFeatureFilter>
    leExclusive?: boolean
    collections?: Array<string>
    from?: string
    to?: string
    minRating?: number
    targetAudience?: Array<App.ExperienceTargetAudience>
    hasFreeCancellation?: boolean
    hasInstantConfirmation?: boolean
    isGift?: boolean
    coordinatesList?: Array<ExperienceCoordinates>
    timeSlots?: Array<Experiences.TimeSlotFilter>
    limit?: number
    countOnly?: boolean
    searchStreaming?: boolean
  } = {},
  options: { force?: boolean } = {},
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { searchStreaming, countOnly, ...searchFilters } = filters
    const key = getExperienceListKey({ placeId, ...filters })
    const existingList = state.experience.experienceLists[key]

    if (existingList && !options.force) {
      return // already fetching or have result for this key
    }

    const mappedCategories = mapSearchStreamingCategories(
      filters.categoryCodes ?? [],
      state.experience.experienceCategories,
    )
    const region = state.geo.currentRegionCode
    const streamFilters: Omit<ExperienceSearchStreamParams, 'placeId' | 'searchId' | 'limit' | 'minOffersRequired' | 'countOnly'> = {
      region,
      categories: mappedCategories ?? [],
      collections: filters.collections,
      luxPlusFeatures: filters.luxPlusFeatures,
      sortBy: filters.sortBy,
      from: filters.from,
      to: filters.to,
      priceGte: filters.priceGte,
      priceLte: filters.priceLte,
      minRating: filters.minRating,
      campaigns: filters.campaigns,
      features: mapSearchStreamingFeatures(filters),
      targetAudience: filters.targetAudience,
      timeSlots: filters.timeSlots,
      bounds: filters.bounds,
    }
    const streamOnSearchPage = !!(
      searchStreaming
      && placeId
      && isExperienceSearchPagePath(state.router.location.pathname)
    )

    if (streamOnSearchPage) {
      dispatch(streamExperienceSearchList(placeId, key, {
        ...streamFilters,
        ...(countOnly || filters.limit == null ? {} : { limit: filters.limit }),
        minOffersRequired: countOnly ? 0 : (filters.limit ?? EXPERIENCE_SEARCH_STREAM_PAGE_SIZE),
        countOnly,
      }))
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_EXPERIENCE_LIST,
      request: async() => {
        if (searchStreaming && placeId) {
          const { experienceIds, total } = await getExperienceSearchStreamList({
            placeId,
            ...streamFilters,
            limit: countOnly ? undefined : filters.limit,
            countOnly,
          })
          return {
            experienceIds,
            total: total ?? experienceIds.length,
          }
        }

        const { experienceIds, offersMetadata } = await getExperienceSearchList({
          placeId,
          region,
          ...searchFilters,
        })

        if (offersMetadata) {
          dispatch(setExperienceOffersMetadata(key, offersMetadata))
        }

        return {
          experienceIds,
          total: experienceIds.length,
        }
      },
      key,
    })
  }
}

const STREAM_DISPATCH_BUFFER_MS = 16

function streamExperienceSearchList(
  placeId: string,
  key: string,
  params: Omit<ExperienceSearchStreamParams, 'placeId' | 'searchId' | 'eventGroupKey'>,
): AppAction {
  return (dispatch, getState) => {
    const eventGroupKey = getExperienceSearchStreamEventGroupKey(key)
    const existingList = getState().experience.experienceLists[key]
    if (existingList?.searchId) {
      clearLastMessage(existingList.searchId)
      removeAllSearchSocketListenersAndSafelyDisconnect({ eventGroupKey })
    }
    addStateListener({ eventGroupKey, listenerId: EXPERIENCE_SEARCH_PAGE_STREAM_LISTENER_ID })

    const searchId = uuidV4()
    let bufferedExperienceIds: Array<string> = []
    let flushBufferedExperienceIdsTimeout: ReturnType<typeof setTimeout> | undefined

    const discardBufferedExperienceIds = () => {
      if (flushBufferedExperienceIdsTimeout) {
        clearTimeout(flushBufferedExperienceIdsTimeout)
        flushBufferedExperienceIdsTimeout = undefined
      }
      bufferedExperienceIds = []
    }

    const flushBufferedExperienceIds = () => {
      if (flushBufferedExperienceIdsTimeout) {
        clearTimeout(flushBufferedExperienceIdsTimeout)
        flushBufferedExperienceIdsTimeout = undefined
      }
      const experienceIdsToFlush = unique(bufferedExperienceIds)
      bufferedExperienceIds = []
      if (!experienceIdsToFlush.length) return

      dispatch({
        type: API_CALL_SUCCESS,
        api: FETCH_EXPERIENCE_LIST,
        key,
        data: {
          streamedExperienceIds: experienceIdsToFlush,
        },
      })
      dispatch(fetchExperiencesById(...experienceIdsToFlush))
    }

    const bufferExperienceId = (experienceId: string) => {
      bufferedExperienceIds.push(experienceId)
      if (flushBufferedExperienceIdsTimeout) return
      flushBufferedExperienceIdsTimeout = setTimeout(flushBufferedExperienceIds, STREAM_DISPATCH_BUFFER_MS)
    }

    dispatch({
      type: API_CALL_REQUEST,
      api: FETCH_EXPERIENCE_LIST,
      key,
      searchId,
    })

    try {
      startExperienceSearchStream({
        placeId,
        searchId,
        eventGroupKey,
        ...params,
      }, {
        onFilters: (total) => {
          if (total == null) return
          dispatch({
            type: API_CALL_SUCCESS,
            api: FETCH_EXPERIENCE_LIST,
            key,
            data: {
              total,
            },
          })
        },
        onOffer: bufferExperienceId,
        onEnd: () => {
          flushBufferedExperienceIds()
          dispatch({
            type: END_EXPERIENCE_LIST_STREAM,
            key,
          })
        },
        onError: (error) => {
          discardBufferedExperienceIds()
          dispatch({
            type: API_CALL_FAILURE,
            api: FETCH_EXPERIENCE_LIST,
            key,
            error: error.message,
          })
        },
        onDisconnect: () => {
          discardBufferedExperienceIds()
          dispatch({
            type: SET_EXPERIENCE_LIST_ERROR,
            key,
            error: 'disconnected',
            socketListenersStatus: 'not-listening',
          })
        },
        onConnect: () => {
          dispatch({
            type: SET_EXPERIENCE_LIST_ERROR,
            key,
            error: undefined,
          })
        },
      })
    } catch(error) {
      dispatch({
        type: API_CALL_FAILURE,
        api: FETCH_EXPERIENCE_LIST,
        key,
        error: error instanceof Error ? error.message : 'Experience search streaming is browser-only',
      })
    }
  }
}

export function stopExperienceSearchListStream(listKey: string): AppAction {
  return (dispatch, getState) => {
    const list = getState().experience.experienceLists[listKey]
    if (!list?.searchId) return

    const eventGroupKey = getExperienceSearchStreamEventGroupKey(listKey)
    removeStateListenerAndMaybePause({
      eventGroupKey,
      listenerId: EXPERIENCE_SEARCH_PAGE_STREAM_LISTENER_ID,
      searchId: list.searchId,
      streamEnded: !!list.streamEnded,
    })

    if (getStateListeners(eventGroupKey).length < 1) {
      dispatch({
        type: SET_EXPERIENCE_LIST_ERROR,
        key: listKey,
        error: list.error,
        socketListenersStatus: 'not-listening',
      })
    }
  }
}

export function getMinExperienceOffers({
  listKey,
  minOffers,
}: {
  listKey: string
  minOffers: number
}): AppAction {
  return (_, getState) => {
    const list = getState().experience.experienceLists[listKey]

    if (
      list?.socketListenersStatus === 'listening' &&
      !list.streamEnded &&
      list.searchId &&
      !list.fetching &&
      list.ids.length < minOffers
    ) {
      extendExperienceSearchStream(list.searchId, minOffers)
    }
  }
}

export interface ExperienceListFilters {
  latitude?: number
  longitude?: number
  currencyCode?: string
  categoryCodes?: Array<number>
  categoryCodesNotIn?: Array<number>
  distanceRange?: Array<string>
  from?: string
  to?: string
  postPurchaseOnly?: boolean
  offerId?: string
  showUnlisted?: boolean
  limit?: number
  offset?: number
  targetAudienceIn?: Array<App.ExperienceTargetAudience>
  onlyCuratedByRegion?: boolean
  hasMemberPrice?: boolean
  experienceIds?: Array<string>
  experienceIdsNotIn?: Array<string>
  bundledPackageId?: string
  isLoyaltyEligible?: boolean
  tagIds?: Array<string>
  providerNotIn?: Array<string>
  curationData?: boolean
}

export function fetchExperienceList(
  filters: ExperienceListFilters = {},
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { airports, currentRegionCode } = state.geo
    const currentCurrency = filters.currencyCode ?? state.geo.currentCurrency
    const key = getExperienceListKey(filters)

    if (state.experience.experienceLists[key]) {
      return // already fetching or have result for this key
    }

    dispatch({
      type: API_CALL,
      api: FETCH_EXPERIENCE_LIST,
      request: async() => {
        const { experiences, total } = await getExperiences({
          currentCurrency,
          filters: {
            latitude: filters.latitude,
            longitude: filters.longitude,
            categoryCodes: filters.categoryCodes,
            categoryCodesNotIn: filters.categoryCodesNotIn,
            distanceRange: filters.distanceRange,
            from: filters.from,
            to: filters.to,
            postPurchaseOnly: filters.postPurchaseOnly,
            hotelOfferId: filters.offerId,
            showUnlisted: filters.showUnlisted,
            limit: filters.limit,
            offset: filters.offset,
            targetAudienceIn: filters.targetAudienceIn,
            onlyCuratedByRegion: filters.onlyCuratedByRegion,
            hasMemberPrice: filters.hasMemberPrice,
            experienceIds: filters.experienceIds,
            experienceIdsNotIn: filters.experienceIdsNotIn,
            bundledPackageId: filters.bundledPackageId,
            isLoyaltyEligible: filters.isLoyaltyEligible,
            tagIds: filters.tagIds,
            providerNotIn: filters.providerNotIn,
            curationData: filters.curationData,
          },
          airports,
          currentRegionCode,
        })
        const experienceIds = experiences.map(exp => exp.id)

        dispatch({
          type: API_CALL_SUCCESS,
          api: FETCH_EXPERIENCES,
          data: { experiences, errors: {} },
          ids: experienceIds,
        })

        return { experienceIds, total }
      },

      key,
    })
  }
}

export function fetchExperiencesById(...ids: Array<string>): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const missingIds = unique(ids.filter(id => !state.experience.experiences[id] && !state.experience.fetchingExperiences[id] && !state.experience.experienceErrors[id]))

    if (missingIds.length === 0) {
      return // all requested ids already in state (fetched, fetching, or errored)
    }

    const { currentCurrency, airports, currentRegionCode, locale } = state.geo

    dispatch({
      type: API_CALL,
      api: FETCH_EXPERIENCES,
      request: () => getExperiencesById(missingIds, { currentCurrency, currentRegionCode, airports, locale }),
      ids: missingIds,
    })
  }
}

export function fetchPassportExperienceTicketsById(...ids: Array<string>): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const missingIds = unique(ids.filter(id => !state.experience.passportTickets[id] && !state.experience.fetchingPassportTickets[id] && !state.experience.passportTicketErrors[id]))

    if (missingIds.length === 0) {
      return
    }

    const { currentCurrency, airports, currentRegionCode, locale } = state.geo
    const customerId = state.auth.account.memberId

    dispatch({
      type: API_CALL,
      api: FETCH_PASSPORT_EXPERIENCE_TICKETS,
      request: () => getPassportExperienceTicketsById(missingIds, { currentCurrency, currentRegionCode, airports, customerId, locale }),
      ids: missingIds,
    })
  }
}

export function clearExperienceById(id: string) {
  return { type: CLEAR_EXPERIENCE, id } as const
}

interface FetchExperienceByIdOptions {
  localeOverride?: string
  /** Bypass the Redux dedup guard so list-cached offers can be upgraded to full translated copy. */
  force?: boolean
}

export function fetchExperienceById(id: string, options?: string | FetchExperienceByIdOptions): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const resolved: FetchExperienceByIdOptions = typeof options === 'string'
      ? { localeOverride: options }
      : (options ?? {})

    if (state.experience.fetchingExperiences[id] || (!resolved.force && (state.experience.experiences[id] || state.experience.experienceErrors[id]))) {
      return // already have it, are currently fetching it, or a previous fetch errored
    }

    const { currentCurrency, airports, currentRegionCode, locale } = state.geo
    const requestLocale = resolved.localeOverride ?? locale

    dispatch({
      type: API_CALL,
      api: FETCH_EXPERIENCE,
      request: () => getExperienceById(id, {
        airports,
        currentRegionCode,
        currentCurrency,
        locale: requestLocale,
      }),
      id,
      locale: requestLocale,
    })
  }
}

interface FetchDatesParams {
  pickupPointId?: string
  redemptionLocationId?: string
  tickets?: Array<string>
  ticketModesMap?: Record<string, string>
  orderId?: string
}

export function fetchExperienceDates(experienceId: string, params?: FetchDatesParams): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const key = getExperienceDatesKey(experienceId, {
      pickupPointId: params?.pickupPointId,
      redemptionLocationId: params?.redemptionLocationId,
      tickets: params?.tickets,
      ticketModesMap: params?.ticketModesMap,
      orderId: params?.orderId,
    })

    if (state.experience.experienceDates[experienceId]?.[key]) return
    dispatch({
      key,
      experienceId,
      type: API_CALL,
      api: FETCH_EXPERIENCE_DATES,
      request: () => getExperienceDateAvailability(experienceId, {
        pickupPointId: params?.pickupPointId,
        redemptionLocationId: params?.redemptionLocationId,
        tickets: params?.tickets,
        ticketModesMap: params?.ticketModesMap,
        orderId: params?.orderId,
      }),
    })
  }
}

interface FetchTimesParams {
  currency?: string
  pickupPointId?: string
  redemptionLocationId?: string
  tickets?: Array<string>
  date?: string
  isBuyNowBookLater?: boolean
  isGift?: boolean
  ignoreCache?: boolean
  ticketModesMap?: Record<string, string>
  orderId?: string
}

export function fetchExperienceTimes(
  experienceId: string,
  params: FetchTimesParams = EmptyObject,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const currencyCode = params.currency ?? state.geo.currentCurrency
    const customerId = state.auth.account.memberId
    const key = getExperienceTimesKey(experienceId, params.date, {
      currency: currencyCode,
      pickupPointId: params.pickupPointId,
      redemptionLocationId: params.redemptionLocationId,
      isBuyNowBookLater: params.isBuyNowBookLater,
      isGift: params.isGift,
      tickets: params.tickets,
      ticketModesMap: params.ticketModesMap,
      orderId: params.orderId,
    })

    if (!params.ignoreCache && state.experience.experienceTimes[experienceId]?.[key]) return

    dispatch({
      key,
      experienceId,
      type: API_CALL,
      api: FETCH_EXPERIENCE_TIMESLOTS,
      request: () => getExperienceTimeAvailability(experienceId, {
        currencyCode,
        pickupPointId: params.pickupPointId,
        redemptionLocationId: params.redemptionLocationId,
        customerId,
        tickets: params.tickets,
        ticketModesMap: params.ticketModesMap,
        date: params.date,
        orderId: params.orderId,
      }),
    })
  }
}

export function clearExperienceTimes(experienceId: string): AppAction {
  return {
    type: CLEAR_EXPERIENCE_TIMES,
    experienceId,
  }
}

export function fetchExperienceCategories(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const locale = state.geo.locale
    const cachedForCurrentLocale = state.experience.experienceCategoriesLocale === locale
    if ((state.experience.experienceCategories.length > 0 && cachedForCurrentLocale) ||
      state.experience.fetchingCategories ||
      (state.experience.experienceCategoriesError && cachedForCurrentLocale)) {
      return // already loaded for this locale, loading, or errored for this locale
    }

    dispatch({
      type: API_CALL,
      api: FETCH_EXPERIENCE_CATEGORIES,
      request: () => getExperienceCategories(locale),
      locale,
    })
  }
}

export function fetchExperienceListFacets(
  placeId: string,
  filters: ExperienceFacetFilters = {},
  evVariant?: string,
  options: { force?: boolean } = {},
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    // Include evVariant in the cache key so the treatment (performance-ordered) and control
    // facet responses never collide in the store. Undefined for control keeps the key unchanged.
    const facetKey = getObjectKey({ placeId, ...filters, evVariant })
    if (state.experience.experienceListFacets[facetKey] && !options.force) {
      return // already fetching or have facets for this place+filters
    }

    dispatch({
      type: API_CALL,
      api: FETCH_EXPERIENCE_LIST_FACETS,
      request: () => getExperienceListFacets(placeId, state.geo.currentRegionCode, filters, evVariant),
      key: facetKey,
    })
  }
}

export function fetchExperienceMetrics(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    if (
      state.experience.experienceMetrics ||
      state.experience.fetchingExperienceMetrics ||
      state.experience.experienceMetricsError
    ) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_EXPERIENCE_METRICS,
      request: () => getExperienceMetrics(state.geo.currentRegionCode),
    })
  }
}

export function fetchExperienceVoucher(experienceItemId: string): AppAction {
  return {
    type: API_CALL,
    experienceItemId,
    api: FETCH_EXPERIENCE_VOUCHER,
    request: () => getExperienceVoucher(experienceItemId),
  }
}

export function fetchBookingDetailsByIds(ids: Array<string>): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const missingIds = ids.filter(id =>
      !state.experience.bookingDetails[id] &&
      !state.experience.fetchingBookingDetails[id] &&
      !state.experience.bookingDetailsErrors[id],
    )
    if (!missingIds.length) return

    dispatch({
      type: API_CALL,
      api: FETCH_BOOKING_DETAILS,
      request: () => getBookingDetailsByIds(missingIds),
      ids: missingIds,
    })
  }
}

export function fetchRefundDetailsByIds(ids: Array<string>): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const missingIds = ids.filter(id =>
      !state.experience.refundDetails[id] &&
      !state.experience.fetchingRefundDetails[id] &&
      !state.experience.refundDetailsErrors[id],
    )
    if (!missingIds.length) return

    dispatch({
      type: API_CALL,
      api: FETCH_REFUND_DETAILS,
      request: () => getOrderItemsRefundDetails(ids),
      ids,
    })
  }
}

export function experienceViewed(experience: App.ExperienceOffer): AppAction {
  addRecentlyViewedExperience(experience.id)
  return {
    type: EXPERIENCE_VIEWED,
    id: experience.id,
    experience,
  }
}

interface ExperiencePlaceOptions {
  placeId?: string
  regionCode?: string
  userSelected?: boolean
  save?: boolean
}

export function setExperiencePlace(type: App.ExperiencePlaceType, options: ExperiencePlaceOptions = {}): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const geo = state.geo

    let placeId: string | undefined
    switch (type) {
      case 'region':
        placeId = options.placeId ?? getPlaceByRegionCode(options.regionCode ?? geo.currentRegionCode).id
        break
      case 'place':
        placeId = options.placeId
        break
      case 'currentLocation':
        try {
          const coords = await getCurrentGeoLocation()
          dispatch({
            type: UPDATE_USER_LAT_LONG,
            latitude: coords.latitude,
            longitude: coords.longitude,
          })
        } catch(message) {
          const intl = getCurrentIntl()
          showSnackbar(message, 'warning', {
            heading: intl.formatMessage({
              id: '7Ve2jl21TjZUihrVgd19',
              defaultMessage: 'Couldn’t find your location!',
              description: 'Snackbar heading when geolocation fails for experiences',
            }),
          })
          return
        }
        break
    }

    if (options.save) {
      setExperienceLocationCookie(type, placeId, options.userSelected)
    } else {
      clearExperienceLocationCookie()
    }

    dispatch({
      type: SET_EXPERIENCE_PLACE,
      placeType: type,
      placeId,
      userSelected: !!options.userSelected,
      save: options.save,
    })
  }
}

export function fetchTransferOptions(experienceId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const existingState = state.experience.experienceTransferOptions[experienceId]
    if (existingState && (existingState.fetching || existingState.error || existingState.transfers)) {
      return
    }
    const customerId = state.auth.account.memberId

    dispatch({
      type: API_CALL,
      api: FETCH_TRANSFER_OPTIONS,
      request: async() => {
        const dates = await getExperienceDateAvailability(experienceId)
        const firstNotSoldOutDate = dates.find(d => !d.soldOut)
        if (!firstNotSoldOutDate) {
          throw new Error('No dates available for transfer')
        }
        return await getExperienceTransferAvailability(experienceId, firstNotSoldOutDate.day, { currencyCode: state.geo.currentCurrency, customerId })
      },
      experienceId,
    })
  }
}

export function setCancellingExperiences(experiences: Array<OrderExperienceViewTicket>): AppAction {
  return {
    type: SET_CANCELLING_EXPERIENCES,
    experiences: experiences.map(view => view.item),
  }
}

export function pollingExperienceVouchers(experienceItemIds: Array<string>): AppAction {
  return async(dispatch, getState) => {
    const state = getState()

    const pendingItems = experienceItemIds.filter(id => {
      const fetching = state.experience.fetchingBookingDetails[id]
      const error = state.experience.bookingDetailsErrors[id]
      const bookingDetails = state.experience.bookingDetails[id]

      return !fetching && !error && !bookingDetails
    })

    if (!pendingItems.length) return

    dispatch({
      type: API_CALL,
      api: FETCH_BOOKING_DETAILS,
      request: () => pollExperiencesVouchers({
        experienceItemIds: pendingItems,
        maxTime: 1000 * 60 * 2, // 2 minutes
        pollInterval: 1000 * 3, // 3 seconds
      }),
      ids: pendingItems,
    })
  }
}

export function fetchTopAttractions(params: { placeName?: string; placeType?: string; lePlaceId?: string; countryCode?: string }): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const key = params.lePlaceId
      ? `top-attractions-place-${params.lePlaceId}`
      : params.countryCode
        ? `top-attractions-country-${params.countryCode}`
        : undefined

    if (!key) return

    const cached = state.experience.topAttractions[key]
    if (cached?.fetching) return
    if (cached && !cached.error && cached.lastFetchedAt && Date.now() - cached.lastFetchedAt < 30_000) return

    dispatch({
      type: API_CALL,
      api: FETCH_TOP_ATTRACTIONS,
      request: async() => {
        const result = await getAttractions({ ...params, sort: 'popularity', limit: 10 })
        return result
      },
      key,
    })
  }
}

export function fetchAttractionOffers(attractionId: string, searchFilters?: Experiences.SearchFilters): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const key = getAttractionOffersKey(attractionId, searchFilters)

    const cachedOffers = state.experience.attractionOffers?.[key]
    if (cachedOffers?.fetching) return
    if (cachedOffers && !cachedOffers.error && cachedOffers.lastFetchedAt && Date.now() - cachedOffers.lastFetchedAt < 30_000) return

    dispatch({
      type: API_CALL,
      api: FETCH_ATTRACTION_OFFERS,
      request: () => getAttractionOffers({
        attractionId,
        limit: 50,
        categoryValues: searchFilters?.categoryCodes,
        luxPlusExclusive: searchFilters?.luxPlusFeatures?.length ? true : undefined,
        priceLte: searchFilters?.priceLte,
        priceGte: searchFilters?.priceGte,
        availableFrom: searchFilters?.checkIn,
        availableTo: searchFilters?.checkOut,
      }),
      key,
    })
  }
}

export function fetchContentTagOffers(destinationId: string, tagId: string): AppAction {
  return (dispatch, getState) => {
    const key = getContentTagOffersKey(destinationId, tagId)
    const cachedOffers = getState().experience.contentTagOffers[key]

    if (cachedOffers?.fetching) return
    if (cachedOffers && !cachedOffers.error && cachedOffers.lastFetchedAt && Date.now() - cachedOffers.lastFetchedAt < 30_000) return

    dispatch({
      type: API_CALL,
      api: FETCH_CONTENT_TAG_OFFERS,
      key,
      request: () => getContentTagOfferIds(tagId, destinationId),
    })
  }
}

export function validateExperiencesCheckoutItems(payload: Experiences.ValidateExperienceItemsPayload): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const allFetchedOrFetching = payload.bookingItems
      .every((item) => state.experience.experienceItemsValidation[item.experienceItemId]?.transactionKey === item.transactionKey)

    // Skip if already fetched or currently fetching
    if (allFetchedOrFetching) {
      return
    }

    dispatch({
      type: API_CALL,
      api: VALIDATE_EXPERIENCE_ITEMS,
      bookingItems: payload.bookingItems,
      request: () => validateExperiencesItems(payload),
    })
  }
}
